"use client";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import dynamic from "next/dynamic";
import Script from "next/script";
import { Icons } from "@assets/assets";
import PrimaryBtn from "@/components/PrimaryBtn";
import { WhitepaperInterface } from "@/lib/Types";
import { savePortfolioAndWhitPaper } from "@/lib/ApiClient";
import { whitePaperAndPortfolioInterface } from "@/lib/Types";
import clsx from "clsx";

const ReCAPTCHA = dynamic(() => import("react-google-recaptcha"), {
  ssr: false,
});
// 1. Import createPortal

// Helpful for cleaner class logic

interface WhitepaperDownloadPopupProps {
  onClose?: () => void;
  whitePaper?: WhitepaperInterface | null;
}

export default function WhitepaperDownloadPopup({
  onClose,
  whitePaper,
}: WhitepaperDownloadPopupProps) {
  const [mounted, setMounted] = useState(false); // 2. State to handle Next.js SSR
  const [isVisible, setIsVisible] = useState(false); // 3. State for animation triggering

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const ensureLoadRecaptcha = () => setLoadRecaptcha(true);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    setMounted(true);
    // 4. Kickstart the animation after mounting
    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    document.body.style.overflow = "hidden";

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = "unset";
    };
  }, []);

  const validateEmail = (value: string) => {
    return /^\S+@\S+\.\S+$/.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!fullName.trim()) {
      setErrorMsg("Please enter your full name.");
      return;
    }
    if (!email.trim() || !validateEmail(email.trim())) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    if (!captchaToken) {
      setErrorMsg("Please complete the reCAPTCHA verification.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: fullName.trim(),
        email: email.trim(),
        captcha: captchaToken,
        pdfUrl: whitePaper?.pdf_file || "1764836570_sample.pdf",
        type: "whitepaper",
      } as whitePaperAndPortfolioInterface;

      const data: any = await savePortfolioAndWhitPaper(payload);

      if (!data?.success) {
        setErrorMsg(data?.message || "Unable to process your request.");
        setLoading(false);
        return;
      }

      setSuccessMsg("Your request has been received. Please check your email.");
      setFullName("");
      setEmail("");
      setCaptchaToken(null);
    } catch (error: any) {
      setErrorMsg(error?.message || "A network error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // 5. Prevent server-side rendering errors
  if (!mounted) return null;

  // 6. Teleport the content to the bottom of <body>
  return createPortal(
    <div
      className={clsx(
        "fixed inset-0 z-[99999] flex items-center justify-center bg-[rgba(0,4,11,0.5)] px-4 py-6 sm:py-10 backdrop-blur-[5px] transition-opacity duration-500",
        isVisible ? "opacity-100" : "opacity-0",
      )}
    >
      <div
        className={clsx(
          "relative w-full max-w-[1100px] overflow-hidden bg-white shadow-[0_30px_90px_rgba(7,13,28,0.3)] md:min-h-[560px] transition-all duration-500 transform",
          isVisible ? "translate-y-0 scale-100" : "translate-y-8 scale-95",
        )}
      >
        <button
          type="button"
          aria-label="Close"
          className="absolute cursor-pointer right-6 top-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/80 text-[#0A0D14] shadow-sm transition hover:bg-white"
          onClick={onClose}
        >
          <span className="relative block h-4 w-4">
            <span className="absolute left-0 top-1/2 block h-[2px] w-full -translate-y-1/2 rotate-45 bg-current" />
            <span className="absolute left-0 top-1/2 block h-[2px] w-full -translate-y-1/2 -rotate-45 bg-current" />
          </span>
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Left Side Content */}
          <div className="lg:flex hidden w-full flex-col bg-[#040C1A] px-8 py-10 text-white flex-1 md:py-12 lg:px-12">
            <div className="relative mb-6 w-full overflow-hidden">
              <Image
                src={`${whitePaper?.baseUrl}${whitePaper?.thumb}`}
                alt="Whitepaper cover"
                className="object-cover max-w-[400px] w-full"
                width={400}
                height={270}
              />
            </div>
            <div className="px-5 py-[8px] bg-white/5 rounded-[60px] outline-1 outline-offset-[-1px] outline-white/10 inline-flex justify-center items-center gap-2.5 w-max">
              <div className="text-white text-sm leading-5">
                {whitePaper?.category || ""}
              </div>
            </div>
            <h2 className="mt-3 clash-font text-[clamp(1.125rem,0.737rem+0.8083vw,2rem)] font-medium leading-tight">
              {whitePaper?.title || ""}
            </h2>
            <p className="mt-3 text-white/70 line-clamp-5">
              {whitePaper?.overview || ""}
            </p>
          </div>

          {/* Right Side - Form */}
          <div className="w-full bg-white px-8 pb-10 lg:pt-[80px] flex-1 md:px-10 py-6">
            <h1 className="clash-font text-[1.9rem] font-medium text-[#0A0D14]">
              Download Whitepaper
            </h1>
            <p className="mt-3 text-base text-[#6B7280]">
              Join our network to get your free copy of the whitepaper and stay
              updated.
            </p>

            <form
              onSubmit={handleSubmit}
              onFocus={ensureLoadRecaptcha}
              className="mt-8 flex flex-col gap-5"
            >
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-[#0A0D14]">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  className="border border-[#E5E7EB] px-4 py-3 text-base text-black outline-none transition-colors placeholder:text-gray-400 focus:border-[#2D5BFF]"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-[#0A0D14]">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="border border-[#E5E7EB] px-4 py-3 text-base text-black outline-none transition-colors placeholder:text-gray-400 focus:border-[#2D5BFF]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {loadRecaptcha && (
                <>
                  <Script
                    id="google-recaptcha"
                    src="https://www.google.com/recaptcha/api.js?render=explicit"
                    strategy="lazyOnload"
                    async
                  />
                  <ReCAPTCHA
                    trigger="focus"
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
                    onChange={(token) => setCaptchaToken(token)}
                  />
                </>
              )}

              {errorMsg && <p className="text-sm text-red-500">{errorMsg}</p>}
              {successMsg && (
                <p className="text-sm text-green-600">{successMsg}</p>
              )}

              <PrimaryBtn
                text={loading ? "Submitting..." : "Download Whitepaper"}
                dark={false}
                iconSrc={Icons?.chevron}
                btnType="submit"
              />
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body, // Close Portal
  );
}
